// @flow
import { useAuthContext } from 'auth/authContext'
import * as React from 'react'
import { Route, useHistory, useLocation, withRouter } from 'react-router-dom'
import { ZentakeContext } from 'utils/ZentakeContext'

import Lockdown from './Lockdown/Lockdown'

type Props = {
  component: React.ComponentType<any>,
  location: any,
  path: string,
  render: (props: any) => React.Node,
  skipLockdown?: boolean,
  route: ?{
    ignoreSetupCard: boolean,
    skipVerifyEmail: boolean,
    skipVerifyClosedAccount?: boolean,
  },
  exact?: boolean,
}

function PrivateRoute({
  component: Component,
  location,
  path,
  render,
  skipLockdown,
  route,
  ...rest
}: Props): React.Node {
  const history = useHistory()

  const { isAuthenticated, loginWithRedirect, paymentWithRedirect } =
    useAuthContext()
  const { teamDetails, userDetails } = React.useContext(ZentakeContext)
  const { pathname } = useLocation()

  React.useEffect(() => {
    const pathname = location.pathname || ''
    const search = location.search || ''
    const targetUrl = pathname + search

    if (!isAuthenticated) {
      loginWithRedirect({
        targetUrl,
      })

      return
    }

    if (!route?.skipVerifyClosedAccount && teamDetails?.is_active === false) {
      history.push('/closed-account')
      return
    }

    if (!route?.skipVerifyEmail && userDetails?.is_verified === false) {
      history.push('/verify-email')
      return
    }

    if (route?.ignoreSetupCard) {
      return
    }

    if (!teamDetails) return

    if (!teamDetails?.has_permission) {
      paymentWithRedirect({ targetUrl })
      return
    }
  }, [
    history,
    isAuthenticated,
    location,
    loginWithRedirect,
    paymentWithRedirect,
    route?.ignoreSetupCard,
    route?.skipVerifyClosedAccount,
    route?.skipVerifyEmail,
    teamDetails,
    userDetails?.is_verified,
  ])

  const renderComponent = (props: any) => {
    if (render) return render(props)

    if (isAuthenticated !== true) return null

    if (!teamDetails) return null

    if (!skipLockdown && !(pathname === '/t' || pathname.startsWith('/t/'))) {
      return (
        <Lockdown>
          <Component {...props} />
        </Lockdown>
      )
    }

    return <Component {...props} />
  }

  return <Route {...rest} path={path} render={renderComponent} />
}

const EnhancedPrivateRoute: React.ComponentType<any> = withRouter(PrivateRoute)

export default EnhancedPrivateRoute
